import type { MouseEvent } from 'react';
import { Handshake } from 'lucide-react';
import { prepaymentReviewNeeded, settledPctOf, taxKindOf } from '@progear/finance-core';
import { cn } from '@/lib/utils';
import {
  FORECAST_PILL_CLS,
  FORECAST_PILL_LABEL,
  FORECAST_PILL_TITLE,
  forecastAmountClass,
  forecastEmphasisClass,
  formatEur,
  isGhostEntry,
  pickMoney,
  PREPAYMENT_REVIEW_CLS,
  PREPAYMENT_REVIEW_GLYPH,
  PREPAYMENT_REVIEW_TITLE,
  PRIORITY_CLS,
  PRIORITY_LABEL,
} from '@/lib/cashFlow';
import { DirectOpenButton } from '../DirectOpenButton';
import { useCalendar } from './context';
import type { PlacedEntry } from './model';

/**
 * One cash-flow entry rendered as a draggable calendar chip. It is pure display:
 * the amount comes from `pickMoney` (selection, not maths — L9), its overdue /
 * forecast / ghost state from the finance-core resolution, and its tax badge from
 * `taxKindOf`. Dragging it to another day routes through the orchestrator (split
 * or reschedule). Hovering opens the group or document floating card.
 */
export function CalendarChip({ placed }: { placed: PlacedEntry }) {
  const { e, r } = placed;
  const { mode, forecastEmphasis, accrual, drag, startDrag, endDrag, showGroupHover, showDocHover, openEntry, today } =
    useCalendar();

  const ghost = isGhostEntry(e);
  const isZeroGhost = ghost && e.money.gross === 0;
  const inGroup = e.groupId !== null;
  const isPart = inGroup && !ghost;
  const overdue = r.carriedForward;
  const forecast = r.effectiveCertainty === 'forecast';
  // The shared "kontrolli" marker on a stale katteta ettemaks — the SAME finance-core
  // decision the register renders in its Staatus cell (L14 parity); never on a ghost
  // (the remainder placeholder would double-flag the same document).
  const reviewNeeded =
    !ghost && prepaymentReviewNeeded(e as unknown as Parameters<typeof prepaymentReviewNeeded>[0], today);
  // Settlement comes from finance-core: a chip is "fully settled" via legacy
  // mark-paid (actualDate) OR cumulative cash + write-offs (settledPctOf ≥ 100);
  // a partial settlement (0 < pct < 100) gets its own % cue (L9 / L14 parity).
  const pct = settledPctOf(e);
  const fullyPaid = e.actualDate !== null || pct >= 100;
  const partial = !fullyPaid && pct > 0;
  const tax = taxKindOf(e);
  const isIncome = e.kind === 'income';
  const amount = pickMoney(e.money, mode);

  const onHover = (ev: MouseEvent) => {
    if (drag) return;
    const pos = { id: e.id, x: ev.clientX, y: ev.clientY };
    if (inGroup) showGroupHover(pos);
    else showDocHover(pos);
  };
  const onLeave = () => {
    showGroupHover(null);
    showDocHover(null);
  };

  return (
    <div
      data-entry={e.id}
      data-ghost={ghost ? 'true' : undefined}
      data-group={e.groupId ?? undefined}
      draggable={!isZeroGhost && !accrual}
      onDragStart={(ev) => {
        // Zero-remainder ghosts have nothing to move; under the accrual basis a
        // reschedule would not move the chip (placement is the document date), so
        // the drag never starts — reschedule/split stay reachable via the register
        // and document detail in both bases (L14).
        if (isZeroGhost || accrual) {
          ev.preventDefault();
          return;
        }
        onLeave();
        startDrag(e);
      }}
      onDragEnd={endDrag}
      onMouseEnter={onHover}
      onMouseMove={onHover}
      onMouseLeave={onLeave}
      title={ghost ? 'Jääk (osamakse jäänud osa)' : overdue ? 'Üle tähtaja' : accrual ? undefined : 'Lohista uuele päevale'}
      className={cn(
        'group/chip flex items-center gap-1 rounded border-l-2 px-1 py-0.5 text-[10px] leading-tight transition-shadow hover:ring-1 hover:ring-ring',
        isZeroGhost || accrual ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
        isIncome
          ? 'border-[hsl(var(--income))] bg-[hsl(var(--income))]/10'
          : 'border-[hsl(var(--cost))] bg-[hsl(var(--cost))]/10',
        forecast && 'border-dashed',
        forecast && !forecastEmphasis && 'opacity-80',
        forecastEmphasisClass(r.effectiveCertainty, forecastEmphasis),
        ghost && 'border-dashed border-foreground/40 bg-foreground/[0.04]',
        isZeroGhost && 'opacity-50',
        overdue && 'pg-hatched',
        // A prioritised entry stays a distinct PROMINENT chip (never folded into the
        // carried-forward group — F2): an inset ring lifts it out of the day's list.
        e.priority && 'ring-1 ring-inset ring-foreground/30',
        drag?.id === e.id && 'opacity-40',
      )}
    >
      <span
        aria-hidden
        className={cn('shrink-0 select-none text-foreground/40', !isZeroGhost && 'group-hover/chip:text-foreground/70')}
      >
        {isZeroGhost ? '·' : '⠿'}
      </span>
      <DirectOpenButton onOpen={() => openEntry(e.id)} label={e.party} />
      {e.priority && (
        <span
          title={`Prioriteet: ${PRIORITY_LABEL[e.priority]}`}
          className={cn('shrink-0 rounded px-0.5 text-[9px] font-bold leading-none', PRIORITY_CLS[e.priority])}
        >
          ⚑
        </span>
      )}
      {e.source === 'payment_plan' && (
        <span aria-hidden title="Maksekokkuleppe osamakse" className="shrink-0 text-[hsl(var(--primary))]">
          <Handshake className="h-3 w-3" />
        </span>
      )}
      {(isPart || (ghost && !isZeroGhost)) && (
        <span
          aria-hidden
          title="Tükeldatud makseplaani osa"
          className="shrink-0 rounded bg-[hsl(var(--vatmode))]/20 px-0.5 text-[9px] font-semibold leading-none text-[hsl(var(--vatmode))]"
        >
          ÷
        </span>
      )}
      {tax && (
        <span
          title={
            tax === 'KMD'
              ? 'Käibedeklaratsioon (riiklik tähtaeg)'
              : 'Tulu- ja sotsiaalmaksu deklaratsioon (riiklik tähtaeg)'
          }
          className="shrink-0 rounded bg-foreground/15 px-1 text-[8px] font-semibold uppercase tracking-wide text-foreground/80"
        >
          {tax}
        </span>
      )}
      {forecast && (
        <span title={FORECAST_PILL_TITLE} className={FORECAST_PILL_CLS}>
          {FORECAST_PILL_LABEL}
        </span>
      )}
      <span className={cn('min-w-0 flex-1 truncate', ghost ? 'text-foreground/55' : 'text-foreground/90')}>
        {e.party}
      </span>
      {isZeroGhost ? (
        <span className="shrink-0 rounded bg-foreground/10 px-1 text-[8px] font-medium uppercase tracking-wide text-foreground/60">
          lükatud
        </span>
      ) : ghost ? (
        <span className="shrink-0 rounded bg-foreground/10 px-1 text-[8px] font-medium uppercase tracking-wide text-foreground/60">
          jääk
        </span>
      ) : null}
      {overdue && (
        <span className="shrink-0 rounded bg-[hsl(var(--cost))]/20 px-1 text-[9px] font-medium leading-none text-[hsl(var(--cost))]">
          → täna
        </span>
      )}
      {reviewNeeded && (
        <span data-review-needed title={PREPAYMENT_REVIEW_TITLE} className={cn('cursor-help', PREPAYMENT_REVIEW_CLS)}>
          {PREPAYMENT_REVIEW_GLYPH}
        </span>
      )}
      {fullyPaid && (
        <span
          title={isIncome ? 'Laekunud (täies mahus)' : 'Makstud (täies mahus)'}
          className="shrink-0 rounded bg-[hsl(var(--income))]/20 px-0.5 text-[8px] font-semibold leading-none text-[hsl(var(--income))]"
        >
          ✓
        </span>
      )}
      {partial && (
        <span
          title={isIncome ? 'Osaliselt laekunud' : 'Osaliselt makstud'}
          className="shrink-0 rounded bg-[hsl(var(--pending))]/20 px-0.5 text-[8px] font-semibold leading-none text-[hsl(var(--pending))]"
        >
          {pct}%
        </span>
      )}
      <span
        className={cn(
          'shrink-0 tabular-nums',
          ghost
            ? 'text-foreground/55'
            : isIncome
              ? 'text-[hsl(var(--income))]'
              : 'text-[hsl(var(--cost))]',
          forecastAmountClass(r.effectiveCertainty),
        )}
      >
        {!ghost && (isIncome ? '+' : '−')}
        {formatEur(amount)}
      </span>
    </div>
  );
}
